// src/map/plateOverlay/imageSource.ts
//
// MapLibre image-source mount/teardown/update lifecycle for PlateOverlayLayer.
//
// Manages:
//   - The plate raster image source + raster layer (SOURCE_ID / LAYER_ID)
//   - Yellow fix-marker debug dots + labels (FIXES_*)
//   - Blob URL lifecycle (revoke previous on swap / on removal — F3 audit)

import maplibregl from 'maplibre-gl';
import type { ImageSource } from 'maplibre-gl';
import { getMapInstance } from '../MapView';
import type { PlateBboxCorners } from '../../lib/plateGeoref';

// ── Constants ────────────────────────────────────────────────────────────────

export const SOURCE_ID = 'plate-overlay';
export const LAYER_ID = 'plate-overlay-raster';
const FIXES_SOURCE_ID = 'plate-overlay-fixes';
const FIXES_DOT_LAYER_ID = 'plate-overlay-fixes-dot';
const FIXES_LABEL_LAYER_ID = 'plate-overlay-fixes-label';

// ── Blob URL lifecycle ────────────────────────────────────────────────────────

// 2026-05-16 perf audit (F3): track the currently-attached blob URL so we
// can revoke the previous one whenever a new plate is swapped in or the
// overlay is removed.
let currentPlateBlobUrl: string | null = null;

export function revokePreviousPlateUrl(): void {
  if (currentPlateBlobUrl && currentPlateBlobUrl.startsWith('blob:')) {
    const prev = currentPlateBlobUrl;
    // Defer to the next frame so MapLibre has a chance to start using
    // the new URL before we yank the old one.
    requestAnimationFrame(() => {
      try { URL.revokeObjectURL(prev); } catch { /* ignore */ }
    });
  }
  currentPlateBlobUrl = null;
}

// ── Fix markers (debug) ──────────────────────────────────────────────────────

export interface ResolvedMatchForMarkers {
  name: string;
  lat: number;
  lon: number;
}

/**
 * Add or update yellow marker dots + labels at the GPS truth positions
 * of each fix that the server matched on this plate. Visual debugging aid:
 * each dot should sit on top of the corresponding fix icon drawn on the plate.
 */
export function addFixMarkersToMap(matches: ResolvedMatchForMarkers[] | undefined): void {
  const map = getMapInstance();
  if (!map) return;
  const features = (matches ?? []).map((m) => ({
    type: 'Feature' as const,
    geometry: { type: 'Point' as const, coordinates: [m.lon, m.lat] },
    properties: { name: m.name },
  }));
  const fc = { type: 'FeatureCollection' as const, features };
  if (map.getSource(FIXES_SOURCE_ID)) {
    (map.getSource(FIXES_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
  } else {
    map.addSource(FIXES_SOURCE_ID, { type: 'geojson', data: fc });
    map.addLayer({
      id: FIXES_DOT_LAYER_ID,
      type: 'circle',
      source: FIXES_SOURCE_ID,
      paint: {
        'circle-radius': 6,
        'circle-color': '#facc15', // amber-400
        'circle-stroke-color': '#000',
        'circle-stroke-width': 1.5,
      },
    });
    map.addLayer({
      id: FIXES_LABEL_LAYER_ID,
      type: 'symbol',
      source: FIXES_SOURCE_ID,
      layout: {
        'text-field': ['get', 'name'],
        'text-size': 11,
        'text-offset': [0, -1.4],
        'text-anchor': 'bottom',
      },
      paint: {
        'text-color': '#facc15',
        'text-halo-color': '#000',
        'text-halo-width': 2,
      },
    });
  }
}

export function removeFixMarkersFromMap(): void {
  const map = getMapInstance();
  if (!map) return;
  if (map.getLayer(FIXES_LABEL_LAYER_ID)) map.removeLayer(FIXES_LABEL_LAYER_ID);
  if (map.getLayer(FIXES_DOT_LAYER_ID)) map.removeLayer(FIXES_DOT_LAYER_ID);
  if (map.getSource(FIXES_SOURCE_ID)) map.removeSource(FIXES_SOURCE_ID);
}

// ── Image source lifecycle ────────────────────────────────────────────────────

export function addOverlayToMap(corners: PlateBboxCorners, dataUrl: string, opacity: number): void {
  const map = getMapInstance();
  if (!map) return;

  // F3: revoke prior plate's blob URL before swapping in the new one.
  if (currentPlateBlobUrl && currentPlateBlobUrl !== dataUrl) {
    revokePreviousPlateUrl();
  }
  currentPlateBlobUrl = dataUrl;

  if (map.getSource(SOURCE_ID)) {
    (map.getSource(SOURCE_ID) as ImageSource).updateImage({ url: dataUrl, coordinates: corners });
    map.setPaintProperty(LAYER_ID, 'raster-opacity', opacity);
  } else {
    map.addSource(SOURCE_ID, { type: 'image', url: dataUrl, coordinates: corners });
    // Insert the plate raster BENEATH the fixes/navaids/airports symbol
    // layers so the database waypoints (blue triangles) render on top
    // of the plate. Useful for visually verifying georef accuracy.
    //
    // Fall back to default (top of stack) if 'fixes-icon' hasn't been
    // mounted yet.
    const beforeId = map.getLayer('fixes-icon') ? 'fixes-icon' : undefined;
    map.addLayer({
      id: LAYER_ID,
      type: 'raster',
      source: SOURCE_ID,
      paint: { 'raster-opacity': opacity, 'raster-fade-duration': 200 },
    }, beforeId);
  }
}

/**
 * Remove the plate raster layer + source + fix markers. Does NOT tear down
 * ownship or route overlays — the PlateOverlayLayer coordinator calls those
 * cleanup functions separately to maintain a predictable cleanup chain.
 */
export function removeOverlayFromMap(): void {
  const map = getMapInstance();
  if (!map) return;
  if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
  removeFixMarkersFromMap();
  // F3: free the blob URL on removal too — not just on swap.
  revokePreviousPlateUrl();
}
